/**
 * Sprint 24.17a — useActiveAgentFor (Pattern B data hook).
 *
 * Reports whether an agent is in-flight on a given entity, for the Live Activity
 * Line. Reads the recent workspace activity feed (system channel) and applies
 * the pure `pickActiveAgentActivity` matcher. Returns null when no fresh work
 * matches.
 *
 * One shared query (stable key) backs every row, so N task rows poll the
 * feed ONCE every 5s, not N times — each row filters the shared result client
 * side. Matches S24.17a.14's "polls every 5s".
 */

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { mcpClient } from "../../lib/mcp-client";
import type { Activity } from "../../lib/types";
import { pickActiveAgentActivity, type LiveActivityState } from "../lib/active-activity";

/** Shared recent system-activity feed — one poll feeds all Live Activity Lines
 * AND the pickup-toast observer (same query key → a single 5s poll). */
export function useSystemActivityFeed() {
  return useQuery<Activity[]>({
    queryKey: ["active-agent-feed"],
    queryFn: async () => {
      const r = await mcpClient.toolsCall<{ activities: Activity[] }>("list_activities", {
        channel: "system",
        limit: 40,
      });
      if (r.isError) throw new Error(r.content?.[0]?.text ?? "Failed to load activity");
      return r.structuredContent?.activities ?? [];
    },
    staleTime: 5_000,
    refetchInterval: 5_000,
  });
}

export function useActiveAgentFor(
  entity_kind: string,
  entity_id: string | undefined,
): LiveActivityState | null {
  const feed = useSystemActivityFeed();
  return useMemo(() => {
    if (!entity_id || !feed.data) return null;
    return pickActiveAgentActivity(feed.data, entity_kind, entity_id);
  }, [feed.data, entity_kind, entity_id]);
}
